export function invert<T extends Record<PropertyKey, PropertyKey>>(obj: T): { [K in keyof T as T[K]]: K } {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [v, k])) as { [K in keyof T as T[K]]: K };
}

export const DataInventoryServiceIds = {
  SFTP05: 1,
  AWS_LAKE: 2,
  AWS_ARBITRATED_PCAPS: 3,
  AWS_RAW_FEEDS: 4,
} as const;

export const DataInventoryServiceNames = {
  SFTP05: 'SFTP05',
  AWS_LAKE: 'AWS Data Lake',
  AWS_ARBITRATED_PCAPS: 'AWS Arbitrated PCAPs',
  AWS_RAW_FEEDS: 'AWS Raw Feeds',
} as const;

export const DataInventoryServiceCodeNames = {
  SFTP05: 'SFTP05',
  AWS_LAKE: 'AWS_LAKE',
  AWS_ARBITRATED_PCAPS: 'AWS_PCAP',
  AWS_RAW_FEEDS: 'AWS_RAW_FEEDS',
} as const;

export const DataInventoryServiceIdToName = {
  [DataInventoryServiceIds.SFTP05]: DataInventoryServiceNames.SFTP05,
  [DataInventoryServiceIds.AWS_LAKE]: DataInventoryServiceNames.AWS_LAKE,
  [DataInventoryServiceIds.AWS_ARBITRATED_PCAPS]: DataInventoryServiceNames.AWS_ARBITRATED_PCAPS,
  [DataInventoryServiceIds.AWS_RAW_FEEDS]: DataInventoryServiceNames.AWS_RAW_FEEDS,
} as const;

export const DataInventoryServiceIdToCodeName = {
  [DataInventoryServiceIds.SFTP05]: DataInventoryServiceCodeNames.SFTP05,
  [DataInventoryServiceIds.AWS_LAKE]: DataInventoryServiceCodeNames.AWS_LAKE,
  [DataInventoryServiceIds.AWS_ARBITRATED_PCAPS]: DataInventoryServiceCodeNames.AWS_ARBITRATED_PCAPS,
  [DataInventoryServiceIds.AWS_RAW_FEEDS]: DataInventoryServiceCodeNames.AWS_RAW_FEEDS,
} as const;

export const UUID_PATTERN = /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/g;

export const PARAMETER_MERGE_SUFFIX = '.MERGE';

export const LICENSE_PERMANENT_DATE_VALUE = 'PERMANENT';
export const LICENSE_PERMANENT_MUST_HAVE_FIELDS = ['host_id=', 'cpus=', 'hostname='];
export const LICENSE_DATE_FORMAT = 'yyyy-MM-dd';
export const LICENSES_INTERNAL_EMAIL_ADDRESS = 'RTUD_Licenses@lseg.com';
export const DISPLAY_ONLY_LICENSE_EXPIRATION_DATE_FORMAT = 'MM-dd-yyyy';
export const SUPPORTED_LICENSE_UPLOAD_DESTINATIONS = {
  AWS_TRANSFER_FAMILY: 'atf',
} as const;

export const WB_PLAN_EXCEED_ACTIONS = {
  shutdown: 'Shutdown',
  charge: 'Keep charging',
} as const;

// TODO: Migrate under DB_CONFIG
export const DB_FLAGS = {
  ENABLE_ROLE_SYNC_NOTIFICATION: 'enable_role_sync_notification',
} as const;

export const DB_CONFIG = {
  AWS_ARBITRATED_PCAPS_FEED_DATE_BOTTLENECK: 'aws_arbitrated_pcaps_feed_dates_bottleneck',
  AWS_ARBITRATED_PCAPS_FEED_DIRS_TO_SKIP: 'aws_arbitrated_pcaps_feed_dirs_to_skip',
  AWS_CONNECTION_RUNNER_NEW_ROLE_MANAGER: 'aws_connection_runner_new_role_manager',
  AWS_LAKE_MT_TOKENS: 'aws_lake_mt_tokens',
  AWS_CLOUDFRONT_ENABLED_S3_BUCKETS: 'aws_cloudfront_enabled_s3_buckets',
} as const;

export const S3_BUCKET_NAME_OR_PREFIX_PATTERN = /^[a-z0-9][a-z0-9.-]{1,62}$/;

export const BuildStatus = {
  READY: 'ready',
  TRIGGERED: 'triggered',
  SKIPPED: 'skipped',
  FINISHED: 'finished',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

// See https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_UserType.html
export const CognitoUserStatus = {
  // User is no longer active. This status isn't actually used by Cognito but is listed in the docs:
  ARCHIVED: 'ARCHIVED',
  // User is disabled due to a potential security threat.
  COMPROMISED: 'COMPROMISED',
  // User is active and has been confirmed:
  CONFIRMED: 'CONFIRMED',
  // The user is confirmed and the user can sign in using a temporary password.
  // On first sign-in, the user must change their password to a new value before doing anything else:
  FORCE_CHANGE_PASSWORD: 'FORCE_CHANGE_PASSWORD',
  // User is confirmed, but must request a code and reset their password before signing in:
  RESET_REQUIRED: 'RESET_REQUIRED',
  // User has registered or been created, but has not confirmed their email address:
  UNCONFIRMED: 'UNCONFIRMED',
  // User status is not known.
  UNKNOWN: 'UNKNOWN',
} as const;

export const TaskExecutionStatus = {
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  FAILED: 'failed',
  INTERRUPTED: 'interrupted',
  RUNNING: 'running',
} as const;

export const AwsLakeDefaultMtTokens = [
  'add_order',
  'add_price_level',
  'aggregated_price_update_snapshot',
  'aggregated_price_update',
  'auction_summary',
  'bbo_quote_snapshot',
  'bbo_quote',
  'cancel_order',
  'clear_orders',
  'clear_price_levels',
  'delete_price_level',
  'error',
  'feed_status',
  'index_update',
  'instruments',
  'leg_announcement',
  'market_status',
  'missing_packets',
  'modify_order',
  'modify_price_level',
  'nbbo_quote',
  'normalizedmpdata',
  'normalizedmpreport',
  'opra_trade_report',
  'order_imbalance',
  'price_level_update',
  'product_announcement',
  'product_statistics',
  'product_status',
  'recovery_begin',
  'recovery_end',
  'refdata',
  'refreport',
  'retail_price_improvement',
  'trade_break',
  'trade_correction',
  'trade',
  'transaction_begin',
  'transaction_end',
] as const;

export const SlaSafeDataInventoryServiceIds = [
  DataInventoryServiceIds.AWS_LAKE,
  DataInventoryServiceIds.AWS_ARBITRATED_PCAPS,
  DataInventoryServiceIds.AWS_RAW_FEEDS,
] as const;

export const DataFeedPermissionTypeToServiceId = {
  'data-feeds-lake': DataInventoryServiceIds.AWS_LAKE,
  'data-feeds-aws-arbitrated-pcaps': DataInventoryServiceIds.AWS_ARBITRATED_PCAPS,
  'data-feeds-aws-raw': DataInventoryServiceIds.AWS_RAW_FEEDS,
} as const;

export const ServiceIdToDataFeedPermissionType = invert(DataFeedPermissionTypeToServiceId);

export const SlaConcerns = ['default'] as const;

export const SlaMarketDayOffsets = [0, -1, -2] as const;

export const DEFAULT_SLA_MARKET_DAY_OFFSET = 0;

export const SlaMarketDayOffsetLabels: Record<number, string> = { 0: 'Same day', [-1]: 'Previous day' };

export const getSlaMarketDayOffsetLabel = (offset: number): string =>
  SlaMarketDayOffsetLabels[offset] ?? `${Math.abs(offset)} days ${offset < 0 ? 'back' : 'ahead'}`;

export const FeedSlaScheduleNamePrefix = 'mace-sla-alert-';

export const HH_MM_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
